import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, 
    CircularProgress, Alert, Paper, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, 
    TablePagination, IconButton, Chip, Avatar, 
    TextField, Select, MenuItem, FormControl, 
    InputLabel, OutlinedInput, InputAdornment,
    Button, Menu, Tooltip, Dialog, DialogTitle,
    DialogContent, DialogActions, Collapse, Checkbox
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as ValidatedIcon,
    Cancel as RejectedIcon,
    Pending as PendingIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Clear as ClearIcon,
    FileDownload as ExportIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import listingService from '../../services/listingService';
import AdminListingDetail from './AdminListingDetail';

/**
 * Composant de gestion de toutes les annonces
 * @returns {React.ReactNode} Interface de gestion complète des annonces
 */
const AllListings = () => {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [categories, setCategories] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedListingId, setSelectedListingId] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [listingToDelete, setListingToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Sélection multiple pour suppression en lot
    const [selectedListings, setSelectedListings] = useState([]);
    const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
    const [batchDeleting, setBatchDeleting] = useState(false);
    
    // Ces définitions ont été remplacées par des versions plus complètes plus bas dans le fichier
    // Les versions à conserver sont celles avec "setSelectedListings(prev => ...)"

    // Ces définitions ont été remplacées par des versions plus complètes plus bas dans le fichier
    // Nous utiliserons les versions qui incluent la vérification de longueur et d'autres validations
    
    const { showNotification } = useNotifications();

    // Options de filtrage
    const statusOptions = [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'pending', label: 'En attente' },
        { value: 'active', label: 'Actives' },
        { value: 'rejected', label: 'Rejetées' },
        { value: 'inactive', label: 'Inactives' },
        { value: 'draft', label: 'Brouillons' },
        { value: 'expired', label: 'Expirées' }
    ];

    const dateOptions = [
        { value: 'all', label: 'Toutes les dates' },
        { value: 'today', label: 'Aujourd\'hui' },
        { value: 'week', label: 'Cette semaine' },
        { value: 'month', label: 'Ce mois' },
        { value: 'quarter', label: 'Ce trimestre' }
    ];

    /**
     * Charger les catégories depuis l'API
     */
    const loadCategories = async () => {
        try {
            const response = await listingService.getCategories();
            if (response.success && response.categories) {
                const categoryOptions = [
                    { value: 'all', label: 'Toutes les catégories' },
                    ...response.categories.map(cat => ({
                        value: cat.slug,
                        label: cat.name
                    }))
                ];
                setCategories(categoryOptions);
            }
        } catch (error) {
            console.error('[AllListings] Error loading categories:', error);
            // Utiliser des catégories par défaut en cas d'erreur
            setCategories([
                { value: 'all', label: 'Toutes les catégories' },
                { value: 'electronique', label: 'Électronique' },
                { value: 'mobilier', label: 'Mobilier' },
                { value: 'vetements', label: 'Vêtements' },
                { value: 'sports', label: 'Sports' }
            ]);
        }
    };

    /**
     * Charger toutes les annonces
     */
    const loadListings = async () => {
        setLoading(true);
        try {
            const params = {
                status: statusFilter,
                limit: rowsPerPage,
                offset: page * rowsPerPage,
                search: searchTerm || undefined,
                category: categoryFilter !== 'all' ? categoryFilter : undefined,
                sort: sortBy,
                sortOrder: sortOrder
            };

            console.log('[AllListings] Loading with params:', params);

            // Utiliser l'endpoint admin pour récupérer toutes les annonces
            const response = await listingService.getAllListings(params);
            
            if (response.success && response.listings) {
                setListings(response.listings);
                setTotalCount(response.pagination?.total || response.listings.length);
                console.log('[AllListings] Loaded', response.listings.length, 'listings');
            } else {
                setListings([]);
                setTotalCount(0);
                showNotification('Aucune annonce trouvée', 'info');
            }
        } catch (error) {
            console.error('[AllListings] Error loading listings:', error);
            setListings([]);
            setTotalCount(0);
            showNotification('Erreur lors du chargement des annonces', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        loadListings();
    }, [page, rowsPerPage, searchTerm, statusFilter, categoryFilter, sortBy, sortOrder]);

    /**
     * Filtrer les annonces (maintenant géré côté serveur)
     */
    const filteredListings = useMemo(() => {
        // Le filtrage est maintenant fait côté serveur
        // On applique juste le filtrage par date côté client si nécessaire
        if (dateFilter === 'all') {
            return listings;
        }

        const now = new Date();
        return listings.filter(listing => {
            const listingDate = new Date(listing.createdAt);
            
            switch (dateFilter) {
                case 'today':
                    return listingDate.toDateString() === now.toDateString();
                case 'week':
                    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    return listingDate >= weekAgo;
                case 'month':
                    return listingDate.getMonth() === now.getMonth() && 
                           listingDate.getFullYear() === now.getFullYear();
                case 'quarter':
                    const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
                    return listingDate >= quarterStart;
                default:
                    return true;
            }
        });
    }, [listings, dateFilter]);

    /**
     * Obtenir le chip de statut
     */
    const getStatusChip = (status) => {
        const statusConfig = {
            pending: { 
                label: 'En attente', 
                color: 'warning', 
                icon: <PendingIcon sx={{ fontSize: 16 }} /> 
            },
            active: { 
                label: 'Active', 
                color: 'success', 
                icon: <ValidatedIcon sx={{ fontSize: 16 }} /> 
            },
            rejected: { 
                label: 'Rejetée', 
                color: 'error', 
                icon: <RejectedIcon sx={{ fontSize: 16 }} /> 
            },
            inactive: { 
                label: 'Inactive', 
                color: 'default', 
                icon: <PendingIcon sx={{ fontSize: 16 }} /> 
            },
            draft: {
                label: 'Brouillon',
                color: 'default',
                icon: <PendingIcon sx={{ fontSize: 16 }} />
            },
            expired: {
                label: 'Expirée',
                color: 'error',
                icon: <RejectedIcon sx={{ fontSize: 16 }} /> 
            }
        };

        const config = statusConfig[status] || statusConfig.pending;
        
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                icon={config.icon}
                variant="outlined"
            />
        );
    };

    /**
     * Formater la date
     */
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Formater le prix
     */
    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    /**
     * Gérer le changement de page
     */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    /**
     * Gérer le changement de lignes par page
     */
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /**
     * Effectuer la recherche manuellement
     */
    const handleSearch = () => {
        setSearchTerm(searchInput);
        setPage(0);
    };

    /**
     * Effacer la recherche
     */
    const handleClearSearch = () => {
        setSearchInput('');
        setSearchTerm('');
        setPage(0);
    };

    /**
     * Gérer la touche Entrée dans le champ de recherche
     */
    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    /**
     * Réinitialiser les filtres
     */
    const resetFilters = () => {
        setSearchInput('');
        setSearchTerm('');
        setStatusFilter('all');
        setCategoryFilter('all');
        setDateFilter('all');
        setSortBy('created_at');
        setSortOrder('desc');
        setPage(0);
    };

    /**
     * Basculer l'expansion d'une ligne
     */
    const toggleRowExpansion = (id) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedRows(newExpanded);
    };

    /**
     * Voir les détails d'une annonce
     */
    const viewDetails = (listing) => {
        setSelectedListingId(listing.id);
        setDetailsOpen(true);
    };

    /**
     * Ouvrir le dialog de confirmation de suppression
     */
    const handleDeleteClick = (listing) => {
        setListingToDelete(listing);
        setDeleteDialogOpen(true);
    };

    /**
     * Gérer la sélection d'une annonce
     */
    const handleSelectListing = (event, listingId) => {
        event.stopPropagation();
        
        setSelectedListings(prev => {
            if (prev.includes(listingId)) {
                return prev.filter(id => id !== listingId);
            } else {
                return [...prev, listingId];
            }
        });
    };
    
    /**
     * Sélectionner ou désélectionner toutes les annonces de la page courante
     */
    const handleSelectAllListings = (event) => {
        if (event.target.checked) {
            const currentPageIds = filteredListings.map(listing => listing.id);
            setSelectedListings(prev => [...new Set([...prev, ...currentPageIds])]);
        } else {
            // Désélectionne uniquement les annonces de la page courante
            const currentPageIds = new Set(filteredListings.map(listing => listing.id));
            setSelectedListings(prev => prev.filter(id => !currentPageIds.has(id)));
        }
    };
    
    /**
     * Ouvrir le dialogue de confirmation pour la suppression en lot
     */
    const handleBatchDeleteClick = () => {
        if (selectedListings.length === 0) {
            showNotification('Veuillez sélectionner au moins une annonce à supprimer', 'warning');
            return;
        }
        
        setBatchDeleteDialogOpen(true);
    };
    
    /**
     * Fermer le dialogue de suppression en lot
     */
    const handleBatchDeleteDialogClose = () => {
        setBatchDeleteDialogOpen(false);
    };
    
    /**
     * Annuler la suppression en lot
     * Note: Cette fonction est équivalente à handleBatchDeleteDialogClose
     */
    const handleBatchDeleteCancel = handleBatchDeleteDialogClose;
    
    /**
     * Confirmer et exécuter la suppression en lot
     */
    const handleBatchDeleteConfirm = async () => {
        if (selectedListings.length === 0) return;
        
        setBatchDeleting(true);
        try {
            console.log('[AllListings] Batch deleting listings:', selectedListings);
            
            // Utiliser l'API pour supprimer les annonces en lot
            await listingService.deleteListingsInBatch(selectedListings);
            
            showNotification(`${selectedListings.length} annonce(s) supprimée(s) avec succès`, 'success');
            
            // Réinitialiser la sélection et fermer le dialogue
            setSelectedListings([]);
            setBatchDeleteDialogOpen(false);
            
            // Recharger les annonces
            loadListings();
        } catch (error) {
            console.error('[AllListings] Error during batch deletion:', error);
            showNotification('Erreur lors de la suppression en lot', 'error');
        } finally {
            setBatchDeleting(false);
        }
    };
    
    /**
     * Confirmer la suppression d'une annonce
     */
    const handleDeleteConfirm = async () => {
        if (!listingToDelete) return;

        setDeleting(true);
        try {
            console.log('[AllListings] Deleting listing:', listingToDelete.id);
            
            // Utiliser l'API pour supprimer l'annonce
            const response = await listingService.deleteListing(listingToDelete.id);
            
            if (response.success) {
                showNotification(`Annonce "${listingToDelete.title}" supprimée avec succès`, 'success');
                
                // Recharger la liste des annonces
                await loadListings();
                
                // Fermer le dialog
                setDeleteDialogOpen(false);
                setListingToDelete(null);
            } else {
                throw new Error(response.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('[AllListings] Error deleting listing:', error);
            showNotification(
                error.message || 'Erreur lors de la suppression de l\'annonce', 
                'error'
            );
        } finally {
            setDeleting(false);
        }
    };

    /**
     * Annuler la suppression
     */
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setListingToDelete(null);
    };

    if (loading) {
        return (
            <Box p={3}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Toutes les Annonces
                </Typography>
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
                Toutes les Annonces
            </Typography>

            {/* Barre de filtres améliorée */}
            <Paper sx={{
                p: 3, mb: 3, position: 'sticky', top: 0, zIndex: 10,
                background: 'linear-gradient(90deg, #f8fafc 60%, #e0e7ff 100%)',
                boxShadow: '0 4px 24px 0 rgba(102,126,234,0.08)',
                borderRadius: 3,
                transition: 'box-shadow 0.3s',
                '&:hover': { boxShadow: '0 8px 32px 0 rgba(102,126,234,0.15)' }
            }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <Box display="flex" gap={1}>
                            <TextField
                                fullWidth
                                placeholder="Rechercher..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={handleSearchKeyPress}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="primary" />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: 2,
                                        background: '#fff',
                                        boxShadow: '0 1px 4px 0 rgba(102,126,234,0.07)',
                                        transition: 'box-shadow 0.2s',
                                        '&:focus-within': { boxShadow: '0 2px 8px 0 rgba(102,126,234,0.13)' }
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSearch}
                                sx={{ minWidth: 'auto', px: 2, boxShadow: 2, borderRadius: 2, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }}
                            >
                                <SearchIcon />
                            </Button>
                            {(searchInput || searchTerm) && (
                                <Button
                                    variant="outlined"
                                    onClick={handleClearSearch}
                                    sx={{ minWidth: 'auto', px: 2, borderRadius: 2, color: 'primary.main', borderColor: 'primary.light' }}
                                >
                                    <ClearIcon />
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Statut</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Statut"
                                onChange={(e) => setStatusFilter(e.target.value)}
                                sx={{ borderRadius: 2, background: '#fff' }}
                            >
                                {statusOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Catégorie</InputLabel>
                            <Select
                                value={categoryFilter}
                                label="Catégorie"
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                sx={{ borderRadius: 2, background: '#fff' }}
                            >
                                {categories.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Période</InputLabel>
                            <Select
                                value={dateFilter}
                                label="Période"
                                onChange={(e) => setDateFilter(e.target.value)}
                                sx={{ borderRadius: 2, background: '#fff' }}
                            >
                                {dateOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box display="flex" gap={1}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={resetFilters}
                                size="small"
                                sx={{ borderRadius: 2, color: 'primary.main', borderColor: 'primary.light' }}
                            >
                                Réinitialiser
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadListings}
                                size="small"
                                sx={{ borderRadius: 2, color: 'primary.main', borderColor: 'primary.light' }}
                            >
                                Actualiser
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ExportIcon />}
                                size="small"
                                sx={{ borderRadius: 2, color: 'primary.main', borderColor: 'primary.light' }}
                            >
                                Exporter
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Résumé des résultats */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                    {filteredListings.length} annonce(s) affichée(s) sur {totalCount} au total
                </Typography>
            </Box>

            {/* Barre d'actions en lot */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="textSecondary">
                    {filteredListings.length} annonce(s) affichée(s) sur {totalCount} au total
                </Typography>
                
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                        {selectedListings.length} annonce(s) sélectionnée(s)
                    </Typography>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={selectedListings.length === 0}
                        onClick={handleBatchDeleteClick}
                        sx={{ 
                            borderRadius: 2,
                            opacity: selectedListings.length > 0 ? 1 : 0.5,
                            transition: 'opacity 0.3s'
                        }}
                    >
                        Supprimer en lot
                    </Button>
                </Box>
            </Box>

            {/* Tableau des annonces */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={selectedListings.length > 0 && selectedListings.length < filteredListings.length}
                                        checked={filteredListings.length > 0 && selectedListings.length === filteredListings.length}
                                        onChange={handleSelectAllListings}
                                    />
                                </TableCell>
                                <TableCell />
                                <TableCell>Annonce</TableCell>
                                <TableCell>Utilisateur</TableCell>
                                <TableCell>Catégorie</TableCell>
                                <TableCell>Prix</TableCell>
                                <TableCell>Statut</TableCell>
                                <TableCell>Date création</TableCell>
                                <TableCell>Vues</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredListings
                                .map((listing) => (
                                    <React.Fragment key={listing.id}>
                                        <TableRow
                                            hover
                                            sx={{
                                                transition: 'box-shadow 0.25s, background 0.25s',
                                                boxShadow: expandedRows.has(listing.id)
                                                    ? '0 4px 24px 0 rgba(102,126,234,0.10)'
                                                    : 'none',
                                                background: expandedRows.has(listing.id) || selectedListings.includes(listing.id)
                                                    ? 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)'
                                                    : 'transparent',
                                                '&:hover': {
                                                    background: 'linear-gradient(90deg, #f0f4ff 0%, #f8fafc 100%)',
                                                    boxShadow: '0 2px 12px 0 rgba(102,126,234,0.10)',
                                                },
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={selectedListings.includes(listing.id)}
                                                    onChange={(event) => handleSelectListing(event, listing.id)}
                                                    onClick={(event) => event.stopPropagation()}
                                                    sx={{ 
                                                        '&.Mui-checked': { 
                                                            color: '#667eea',
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleRowExpansion(listing.id)}
                                                    sx={{
                                                        transition: 'transform 0.2s',
                                                        transform: expandedRows.has(listing.id) ? 'rotate(90deg)' : 'none',
                                                        color: expandedRows.has(listing.id) ? 'primary.main' : 'inherit',
                                                    }}
                                                >
                                                    {expandedRows.has(listing.id) ? 
                                                        <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="subtitle2" noWrap>
                                                        {listing.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary" noWrap>
                                                        {listing.description ? listing.description.substring(0, 50) + '...' : 'Aucune description'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar sx={{ width: 36, height: 36, boxShadow: 2, border: '2px solid #e0e7ff', transition: 'box-shadow 0.2s, border 0.2s', '&:hover': { boxShadow: 4, border: '2px solid #667eea' } }}>
                                                        {listing.user?.name?.[0] || '?'}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {listing.user?.name || 'Utilisateur supprimé'}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {listing.user?.email || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={categories.find(c => c.value === listing.category)?.label || listing.categoryName || 'Autre'} 
                                                    size="small" 
                                                    variant="outlined"
                                                    sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', color: '#667eea' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                    {formatPrice(listing.price)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {getStatusChip(listing.status)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(listing.createdAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {listing.views}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={0.5}>
                                                    <Tooltip title="Voir détails">
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => viewDetails(listing)}
                                                            sx={{
                                                                transition: 'transform 0.18s, box-shadow 0.18s',
                                                                boxShadow: 1,
                                                                bgcolor: '#e0e7ff',
                                                                color: '#667eea',
                                                                '&:hover': {
                                                                    transform: 'scale(1.15)',
                                                                    boxShadow: 3,
                                                                    bgcolor: '#667eea',
                                                                    color: '#fff',
                                                                },
                                                            }}
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Modifier">
                                                        <IconButton size="small"
                                                            sx={{
                                                                transition: 'transform 0.18s, box-shadow 0.18s',
                                                                boxShadow: 1,
                                                                bgcolor: '#e0e7ff',
                                                                color: '#764ba2',
                                                                '&:hover': {
                                                                    transform: 'scale(1.15)',
                                                                    boxShadow: 3,
                                                                    bgcolor: '#764ba2',
                                                                    color: '#fff',
                                                                },
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Supprimer">
                                                        <IconButton 
                                                            size="small" 
                                                            color="error"
                                                            onClick={() => handleDeleteClick(listing)}
                                                            sx={{
                                                                transition: 'transform 0.18s, box-shadow 0.18s',
                                                                boxShadow: 1,
                                                                bgcolor: '#fff0f0',
                                                                color: '#e53935',
                                                                '&:hover': {
                                                                    transform: 'scale(1.15)',
                                                                    boxShadow: 3,
                                                                    bgcolor: '#e53935',
                                                                    color: '#fff',
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                        {/* Ligne de détails expandable */}
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0, background: '#f8fafc' }} colSpan={9}>
                                                <Collapse in={expandedRows.has(listing.id)} timeout={400} unmountOnExit sx={{ transition: 'all 0.4s cubic-bezier(.4,2,.6,1)' }}>
                                                    <Box sx={{ margin: 2, p: 2, borderRadius: 2, background: 'linear-gradient(90deg, #e0e7ff 0%, #f8fafc 100%)', boxShadow: 2 }}>
                                                        <Typography variant="h6" gutterBottom component="div" color="#667eea">
                                                            Détails de l'annonce
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2">
                                                                    <strong>Description complète:</strong><br />
                                                                    {listing.description || 'Aucune description disponible'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="body2">
                                                                    <strong>Images:</strong> {listing.images?.length || 0} photo(s)
                                                                </Typography>
                                                                {listing.validatedAt && (
                                                                    <Typography variant="body2">
                                                                        <strong>Validée le:</strong> {formatDate(listing.validatedAt)}
                                                                        <br />
                                                                        <strong>Par:</strong> {listing.validatedBy?.name}
                                                                    </Typography>
                                                                )}
                                                                {listing.rejectedAt && (
                                                                    <Typography variant="body2" color="error">
                                                                        <strong>Rejetée le:</strong> {formatDate(listing.rejectedAt)}
                                                                        <br />
                                                                        <strong>Par:</strong> {listing.rejectedBy?.name}
                                                                        <br />
                                                                        <strong>Raison:</strong> {listing.rejectionReason}
                                                                    </Typography>
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Lignes par page:"
                    labelDisplayedRows={({ from, to, count }) => 
                        `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                    }
                />
            </Paper>

            {/* Détails de l'annonce sélectionnée */}
            {selectedListingId && (
                <AdminListingDetail
                    listingId={selectedListingId}
                    open={detailsOpen}
                    onClose={() => {
                        setDetailsOpen(false);
                        setSelectedListingId(null);
                    }}
                    onRefresh={loadListings}
                />
            )}

            {/* Dialog de confirmation de suppression */}
            <Dialog
                open={deleteDialogOpen}
                onClose={!deleting ? handleDeleteCancel : undefined}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ 
                    color: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <DeleteIcon />
                    Confirmer la suppression
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            ⚠️ Action irréversible
                        </Typography>
                        <Typography variant="body2">
                            Cette action ne peut pas être annulée. L'annonce sera définitivement supprimée.
                        </Typography>
                    </Alert>
                    
                    {listingToDelete && (
                        <Box>
                            <Typography variant="body1" paragraph>
                                Êtes-vous sûr de vouloir supprimer l'annonce suivante ?
                            </Typography>
                            
                            <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                <Typography variant="h6" gutterBottom>
                                    {listingToDelete.title}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {listingToDelete.description ? 
                                        listingToDelete.description.substring(0, 100) + '...' : 
                                        'Aucune description'
                                    }
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} mt={1}>
                                    <Chip 
                                        label={`ID: ${listingToDelete.id.slice(0, 8)}...`} 
                                        size="small" 
                                        variant="outlined"
                                    />
                                    <Chip 
                                        label={listingToDelete.user?.name || 'Utilisateur supprimé'} 
                                        size="small" 
                                        variant="outlined"
                                        color="primary"
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handleDeleteCancel}
                        disabled={deleting}
                        variant="outlined"
                        size="large"
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                        size="large"
                    >
                        {deleting ? 'Suppression...' : 'Supprimer définitivement'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de confirmation de suppression par lots */}
            <Dialog
                open={batchDeleteDialogOpen}
                onClose={!batchDeleting ? handleBatchDeleteDialogClose : undefined}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ 
                    color: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <DeleteIcon />
                    Confirmer la suppression multiple
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            ⚠️ Action irréversible
                        </Typography>
                        <Typography variant="body2">
                            Cette action ne peut pas être annulée. Toutes les annonces sélectionnées seront définitivement supprimées.
                        </Typography>
                    </Alert>
                    
                    <Box>
                        <Typography variant="body1" paragraph>
                            Vous êtes sur le point de supprimer <strong>{selectedListings.length} annonce(s)</strong>.
                        </Typography>
                        
                        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                            <Typography variant="body1">
                                ID des annonces sélectionnées :
                            </Typography>
                            <Box sx={{ mt: 1, maxHeight: '150px', overflowY: 'auto' }}>
                                {selectedListings.map(id => (
                                    <Chip 
                                        key={id}
                                        label={`ID: ${id.slice(0, 8)}...`} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ m: 0.5 }}
                                    />
                                ))}
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handleBatchDeleteDialogClose}
                        disabled={batchDeleting}
                        variant="outlined"
                        size="large"
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleBatchDeleteConfirm}
                        disabled={batchDeleting}
                        startIcon={batchDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                        size="large"
                    >
                        {batchDeleting ? 'Suppression...' : `Supprimer ${selectedListings.length} annonce(s)`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AllListings;
